import { useState, useEffect } from 'react';

export type ConnectionType = 'wifi' | 'cellular' | 'unknown';

interface NetworkInfo {
  connectionType: ConnectionType;
  effectiveType: string | undefined;
  saveData: boolean;
}

/**
 * Hook to detect network connection type and characteristics
 */
export const useNetworkInfo = () => {
  const [networkInfo, setNetworkInfo] = useState<NetworkInfo>({
    connectionType: 'unknown',
    effectiveType: undefined,
    saveData: false
  });

  useEffect(() => {
    const updateNetworkInfo = () => {
      // TypeScript doesn't have full typing for Network Information API
      const connection = (navigator as any).connection || 
                        (navigator as any).mozConnection || 
                        (navigator as any).webkitConnection;

      if (connection) {
        // Determine connection type
        let connectionType: ConnectionType = 'unknown';
        
        // Check if on cellular data
        if (connection.type === 'cellular' || 
            connection.effectiveType === '2g' || 
            connection.effectiveType === '3g' ||
            connection.effectiveType === 'slow-2g') {
          connectionType = 'cellular';
        }
        // Check if on WiFi
        else if (connection.type === 'wifi' || 
                 connection.effectiveType === '4g') {
          connectionType = 'wifi';
        }
        // Fallback: assume WiFi if connection is fast
        else if (connection.downlink > 2) {
          connectionType = 'wifi';
        }

        setNetworkInfo({
          connectionType,
          effectiveType: connection.effectiveType,
          saveData: connection.saveData || false
        });
      } else {
        // Network Information API not available
        // Try to detect from online status
        setNetworkInfo({
          connectionType: navigator.onLine ? 'unknown' : 'unknown',
          effectiveType: undefined,
          saveData: false
        });
      }
    };

    // Initial check
    updateNetworkInfo();

    // Listen for connection changes
    const connection = (navigator as any).connection || 
                      (navigator as any).mozConnection || 
                      (navigator as any).webkitConnection;

    if (connection) {
      connection.addEventListener('change', updateNetworkInfo);
      
      return () => {
        connection.removeEventListener('change', updateNetworkInfo);
      };
    }

    // Fallback: listen to online/offline events
    window.addEventListener('online', updateNetworkInfo);
    window.addEventListener('offline', updateNetworkInfo);

    return () => {
      window.removeEventListener('online', updateNetworkInfo);
      window.removeEventListener('offline', updateNetworkInfo);
    };
  }, []);

  return networkInfo;
};
